import React from 'react';
import { CheckCircle, XCircle, X } from 'lucide-react';
import { AccountPosition } from '../types';

interface PositionActionsProps {
  position: AccountPosition;
  updating: string | null;
  handleStatusUpdate: (
    positionId: string,
    newStatus: string,
    notes?: string,
  ) => void;
  handleCancelPosition: (positionId: string) => Promise<void>;
}

const PositionActions: React.FC<PositionActionsProps> = ({
  position,
  updating,
  handleStatusUpdate,
  handleCancelPosition,
}) => {
  // Check if position can be cancelled
  const canCancel = [
    'RECOMMENDED',
    'APPROVED',
    'ENTRY_PENDING',
    'ACTIVE',
    'PENDING_MARKET_OPEN',
    'ORDERS_REJECTED',
  ].includes(position.status);

  const handleCancel = async () => {
    if (
      !window.confirm(
        'Are you sure you want to cancel this position? This will cancel any pending orders and close any active positions.',
      )
    ) {
      return;
    }

    try {
      await handleCancelPosition(position._id);
    } catch (error) {
      console.error('Error cancelling position:', error);
      alert('Failed to cancel position. Please try again.');
    }
  };

  return (
    <>
      <div className="flex items-center text-sm text-gray-500 space-x-4">
        <span>Created: {new Date(position.createdAt).toLocaleString()}</span>
        {position.expiresAt && (
          <span>Expires: {new Date(position.expiresAt).toLocaleString()}</span>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-2 mt-4">
        {position.status === 'RECOMMENDED' && (
          <>
            <button
              onClick={() => handleStatusUpdate(position._id, 'APPROVED')}
              disabled={updating === position._id}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {updating === position._id ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Approve
            </button>

            <button
              onClick={() => {
                const notes = window.prompt(
                  'Enter rejection reason (optional):',
                );
                handleStatusUpdate(
                  position._id,
                  'REJECTED',
                  notes || undefined,
                );
              }}
              disabled={updating === position._id}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {updating === position._id ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject
            </button>
          </>
        )}

        {canCancel && (
          <button
            onClick={handleCancel}
            disabled={updating === position._id}
            className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
          >
            {updating === position._id ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <X className="w-4 h-4 mr-2" />
            )}
            Cancel
          </button>
        )}
      </div>
    </>
  );
};

export default PositionActions;
